import { ICompanyProfileLookup } from '../../../services/queries/SourcingCompanyQueries';

/**
 * Translate a free-form `:companyId` URL segment into the structured lookup
 * payload expected by the sourcing `/api/v1/company-profile` endpoint.
 *
 * The route historically carried a slug (e.g. `anthropic`) when the page was
 * fed by mocks. The real API has no notion of slugs — it accepts one of:
 * domain, LinkedIn URL, Crunchbase UUID, Harmonic entity URN, Harmonic id
 * (int), or PitchBook id. We therefore inspect the shape of the segment and
 * pick the most specific match; if nothing matches, we fall back to `domain`
 * which is what the grid exposes most reliably.
 *
 * Pure function — easy to unit-test with the table of examples below.
 *
 *  | input                                         | lookup             |
 *  | --------------------------------------------- | ------------------ |
 *  | "anthropic.com"                              | { domain }         |
 *  | "https://www.anthropic.com/"                 | { domain }         |
 *  | "urn:harmonic:company:12345"                 | { hmEntityUrn }    |
 *  | "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d"       | { cbUuid }         |
 *  | "linkedin.com/company/anthropic"             | { linkedinUrl }    |
 *  | "12345"                                      | { hmId: 12345 }    |
 *  | "anthropic"   (no dot, not a uuid/urn)       | { domain }         |
 */
export function resolveCompanyLookup(rawCompanyId: string): ICompanyProfileLookup {
  const trimmed = rawCompanyId.trim();
  if (!trimmed) {
    return {};
  }

  // Decode in case the value was URL-encoded when building the route.
  const decoded = safeDecode(trimmed);

  if (isHarmonicEntityUrn(decoded)) {
    return { hmEntityUrn: decoded };
  }

  if (isUuid(decoded)) {
    return { cbUuid: decoded };
  }

  if (isLinkedinUrl(decoded)) {
    return { linkedinUrl: decoded };
  }

  if (isPositiveInteger(decoded)) {
    return { hmId: Number.parseInt(decoded, 10) };
  }

  // Anything else — with or without a dot — is treated as a domain. The
  // backend normalizes protocol / www / trailing slashes, so we just strip
  // the explicit protocol to keep URL encoding friendly.
  return { domain: normalizeAsDomain(decoded) };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const POSITIVE_INT_RE = /^\d+$/;

function isHarmonicEntityUrn(value: string): boolean {
  return value.toLowerCase().startsWith('urn:harmonic:');
}

function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

function isLinkedinUrl(value: string): boolean {
  // Covers both bare slugs like "linkedin.com/company/anthropic" and fully
  // qualified URLs. The backend already handles normalization.
  return /linkedin\.com\/company\//i.test(value);
}

function isPositiveInteger(value: string): boolean {
  return POSITIVE_INT_RE.test(value);
}

function normalizeAsDomain(value: string): string {
  return value
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed URI sequences — fall back to the raw value rather than throwing.
    return value;
  }
}
